import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import throttle from 'lodash/throttle';
import { useIntl } from 'react-intl';

import { LIST_EVENTS_RESOURCE, useListEvents } from 'src/features/eventLog/api/useListEvents';
import CellEventLogMessage from 'src/features/eventLog/components/cell/CellEventLogMessage';
import CellEventCustomer from 'src/features/eventLog/components/cell/CellEventCustomer';
import { eventTypeBadgeOptions } from 'src/features/eventLog/utils/constants';
import { eventMessages } from 'src/features/eventLog/utils/messages';
import { EventLog } from 'src/features/eventLog/types';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import useSearch from 'src/registry/hooks/useSearch';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent } from 'src/features/visitor/types';
import { NEW_STAY_AND_PASS_EVENTS } from 'src/features/visitor/utils/wsEvents';
import { queryClient } from 'src/lib/react-query';

export default function EventLogTable() {
    const { formatMessage } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'timestamp', desc: true }] });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();

    const { subscribe, unsubscribe } = React.useContext(WSContext);

    React.useEffect(() => {
        const handleWebSocket = throttle((event: WebSocketEvent) => {
            if (NEW_STAY_AND_PASS_EVENTS.includes(event.message)) {
                void queryClient.invalidateQueries({
                    queryKey: [LIST_EVENTS_RESOURCE],
                });
            }
        }, 500);
        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => {
            handleWebSocket.cancel();
            unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
        };
    }, [subscribe, unsubscribe]);

    const { parkingId } = useParking();

    const {
        data: eventLogData,
        isError,
        isLoading,
    } = useListEvents({
        parkingId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        ...queryFiltersOptions,
    });

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const columnHelper = createColumnHelper<EventLog>();

    const columns = [
        columnHelper.accessor('timestamp', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(commonMessages.time),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
            },
            maxSize: 120,
        }),
        columnHelper.accessor('event_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(eventMessages.type),
                badgeOptions: eventTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('event_data', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => <CellEventCustomer eventData={row.original.event_data} event={row.original} />,
            meta: {
                title: formatMessage(commonMessages.customer),
            },
        }),
        columnHelper.accessor('id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => <CellEventLogMessage eventData={row.original.event_data} event={row.original} />,
            meta: {
                title: formatMessage(eventMessages.message),
            },
        }),
    ];

    return (
        <DataTable<EventLog, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={eventLogData}
            pagination={pagination}
            sorting={sorting}
            search={search}
            filters={filters}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(eventMessages.eventLog),
                entryName: formatMessage(eventMessages.eventLog).toLowerCase(),
                entryNames: formatMessage(eventMessages.eventLogs).toLowerCase(),
            }}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
            enableLocalFilters={false}
        />
    );
}
